import exceljs from "exceljs";
import { saveAs } from "file-saver";
import { useLocation, useNavigate, useOutletContext } from "react-router-dom";
import { useAuthStore } from "src/components/main/authStore";
import { mimeTypeMap, MyDate } from "src/components/common/functions";
import { Billing, BillingOutletContext, parseApproval } from "./types";

import { useUiStore } from "src/components/main/uiStore";
export default function BillingsExportRoute() {
    const user = useAuthStore(state => state.user);
    const setShowLoader = useUiStore(state => state.setShowLoader);
    const navigate = useNavigate();
    const location = useLocation();
    const { billings } = useOutletContext<BillingOutletContext>();

    const exportFunc = async () => {
        setShowLoader(true);
        try {
            const workbook = new exceljs.Workbook();
            workbook.creator = user?.userName ?? "Unknown";
            workbook.created = new Date();
            workbook.calcProperties.fullCalcOnLoad = true;
            const worksheet = workbook.addWorksheet("Billings", { views: [{ showGridLines: false }] });
            worksheet.state = "visible";
            worksheet.properties.defaultColWidth = 22.57;
            const rows = billings.map((billing: Billing) => {
                const approvals = parseApproval(billing.approval);
                return [
                    billing.billingNumber,
                    billing.projectName ?? "",
                    billing.fromDate ? new Date(billing.fromDate) : "",
                    billing.toDate ? new Date(billing.toDate) : "",
                    Number.parseFloat(billing.amount || "0"),
                    billing.submitter ?? "",
                    billing.submitTime ? new Date(billing.submitTime.replace(/-/g, "/")) : "",
                    billing.updater ?? "",
                    billing.updateTime ? new Date(billing.updateTime.replace(/-/g, "/")) : "",
                    billing.pdfId ? `https://drive.google.com/uc?export=download&id=${billing.pdfId}` : "",
                    billing.decision ?? "",
                    billing.billed ? "yes" : "no",
                    billing.notes ?? "",
                    approvals[0]?.approverId ?? "",
                    approvals[0]?.approverDecision ?? "",
                    approvals[0]?.reviewTime ? new Date(approvals[0].reviewTime!.replace(/-/g, "/")) : "",
                    approvals[1]?.approverId ?? "",
                    approvals[1]?.approverDecision ?? "",
                    approvals[1]?.reviewTime ? new Date(approvals[1].reviewTime!.replace(/-/g, "/")) : "",
                    billing.void ?? "",
                    billing.voidReason ?? "",
                    billing.notify ?? "",
                ];
            });
            worksheet.addTable({
                name: "BillingsTable",
                ref: "A1",
                totalsRow: true,
                style: { showRowStripes: true },
                columns: [
                    { name: "Billing Number", filterButton: true },
                    { name: "Project Name", filterButton: true },
                    { name: "Start Date", filterButton: true },
                    { name: "End Date", filterButton: true },
                    { name: "Amount", filterButton: true, totalsRowFunction: "sum" },
                    { name: "Submitter", filterButton: true },
                    { name: "Submit Time", filterButton: true },
                    { name: "Updater", filterButton: true },
                    { name: "Update Time", filterButton: true },
                    { name: "PDF", filterButton: true },
                    { name: "Status", filterButton: true },
                    { name: "Billed", filterButton: true },
                    { name: "Notes", filterButton: true },
                    { name: "Approver 1 Name", filterButton: true },
                    { name: "Approver 1 Decision", filterButton: true },
                    { name: "Approver 1 Review Time", filterButton: true },
                    { name: "Approver 2 Name", filterButton: true },
                    { name: "Approver 2 Decision", filterButton: true },
                    { name: "Approver 2 Review Time", filterButton: true },
                    { name: "Void", filterButton: true },
                    { name: "Void Reason", filterButton: true },
                    { name: "Notify", filterButton: true },
                ],
                rows,
            });
            const buffer = await workbook.xlsx.writeBuffer();
            saveAs(new Blob([buffer], { type: mimeTypeMap.xlsx }), `billing export ${(new MyDate()).s()}.xlsx`);
            navigate({ pathname: "..", search: location.search }, { replace: true });
        } finally {
            setShowLoader(false);
        }
    };

    return (
        <div className="px-3 pt-3">
            <div className="rounded border p-3" style={{ maxWidth: 520 }}>
                <h4 className="mb-2">Export Billings</h4>
                <div className="mb-3">Export {billings.length} billings from the current filtered result.</div>
                <div className="d-flex gap-2">
                    <button type="button" className="btn btn-success" onClick={exportFunc}>
                        Export
                    </button>
                    <button
                        type="button"
                        className="btn btn-dark"
                        onClick={() => navigate({ pathname: "..", search: location.search }, { replace: true })}
                    >
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
}
